import { ChangeDetectionStrategy, Component, computed, input, output, signal } from '@angular/core'
import { PaymentInstrumentDisplayComponent } from '../../../shared/components/payment/payment-instrument-display/payment-instrument-display.component';
import { ISubscriptionPaymentTransaction } from '../../../shared/interfaces/[Model-based]';
import { PaymentTransactionReversalStatus_, PaymentTransactionStatus_, PaymentTransactionType_ } from '@appShared/services/lookup/[CodeGen]';
import { CurrencyPipe, DatePipe } from '@angular/common';

@Component({
   selector: 'app-payment-transactions',
   templateUrl: './payment-transactions.component.html',
   styleUrls: ['./payment-transactions.component.less'],
   imports: [PaymentInstrumentDisplayComponent, CurrencyPipe, DatePipe],
   changeDetection: ChangeDetectionStrategy.OnPush
})
export class PaymentTransactionsComponent {
   public readonly subscriptionPaymentTransactions = input<ISubscriptionPaymentTransaction[]>()

   public readonly saleSubscriptionPaymentTransactions = computed(() => {
      return this.subscriptionPaymentTransactions()?.filter(spt => spt.paymentTransaction?.typeCode === PaymentTransactionType_.Sale) ?? []
   })

   /** Driven by the parent's store: true while a reversal is in flight. */
   public readonly isRefunding = input<boolean>(false)
   public readonly refund = output<ISubscriptionPaymentTransaction>()

   /* expose enums to the template */
   public readonly PaymentTransactionStatus_ = PaymentTransactionStatus_
   public readonly PaymentTransactionType_ = PaymentTransactionType_
   public readonly PaymentTransactionReversalStatus_ = PaymentTransactionReversalStatus_

   /** The transaction currently awaiting inline reversal confirmation, if any. */
   public readonly pendingRefund = signal<ISubscriptionPaymentTransaction | null>(null)

   /**
    * Whether the rep can request a reversal for this transaction: an approved
    * charge (Sale/Deposit) with no reversal already in flight or completed —
    * a deferred reversal that failed CAN be resubmitted. Mirrors (but does not
    * replace) the server-side validation.
    */
   canReverse(subPaymentTransaction: ISubscriptionPaymentTransaction): boolean {
      const transaction = subPaymentTransaction?.paymentTransaction
      if (!transaction) {
         return false
      }

      if (transaction.statusCode !== PaymentTransactionStatus_.Approved) {
         return false
      }
      if (transaction.typeCode !== PaymentTransactionType_.Sale &&
          transaction.typeCode !== PaymentTransactionType_.Deposit) {
         return false
      }

      const reversal = transaction.paymentTransactionReversalOriginalPaymentTransaction
      if (reversal && reversal.statusCode !== PaymentTransactionReversalStatus_.Failed) {
         // Pending/Processing (in flight), Succeeded (done), or Rejected (returned unpaid)...
         return false
      }

      // Already reversed/returned via a sibling transaction referencing this one?
      const siblings = this.subscriptionPaymentTransactions() ?? []
      const alreadyReversed = siblings.some(sibling => {
         const siblingTransaction = sibling.paymentTransaction
         return siblingTransaction &&
            siblingTransaction !== transaction &&
            siblingTransaction.referenceSourceId === transaction.sourceId &&
            (siblingTransaction.typeCode === PaymentTransactionType_.ReturnItem ||
               ((siblingTransaction.typeCode === PaymentTransactionType_.Credit ||
                 siblingTransaction.typeCode === PaymentTransactionType_.Void) &&
                siblingTransaction.statusCode === PaymentTransactionStatus_.Approved))
      })

      return !alreadyReversed
   }

   requestRefund(transaction: ISubscriptionPaymentTransaction): void {
      this.pendingRefund.set(transaction)
   }

   cancelRefund(): void {
      this.pendingRefund.set(null)
   }

   confirmRefund(transaction: ISubscriptionPaymentTransaction): void {
      // Keep the prompt open (buttons disable via isRefunding) and emit; the
      // parent calls closeRefundPrompt() once the call settles.
      this.refund.emit(transaction)
   }

   /** Called by the parent after the reversal settles, to close the inline prompt. */
   closeRefundPrompt(): void {
      this.pendingRefund.set(null)
   }
}
